import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, TableShell, Field, Drawer } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const empty = {
  make: "", model: "", year: 2024, plate: "", capacity: 4, wheelchair_accessible: false, stretcher_capable: false,
  status: "active", speed_limit_mph: "",
  vin: "", wheelchair_equipment: "none", wheelchair_positions: "", protected_transport: false, ownership: "",
  fleet_number: "", plate_state: "MN", gvwr: "",
};

// Every 2-letter US state/territory abbreviation, plus MN first since it's the overwhelming
// default for an MnDOT STS provider's own fleet.
const PLATE_STATES = ["MN", "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID",
  "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MS", "MO", "MT", "NE", "NV", "NH",
  "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
  "VA", "WA", "WV", "WI", "WY"].map((s) => ({ value: s, label: s }));

const STATUS = [
  { value: "active", label: "Active" },
  { value: "maintenance", label: "Maintenance" },
  { value: "retired", label: "Retired" },
];
const WHEELCHAIR_EQUIPMENT = [
  { value: "none", label: "None" },
  { value: "ramp", label: "Ramp" },
  { value: "lift", label: "Lift" },
];
const OWNERSHIP = [
  { value: "", label: "Select" },
  { value: "carrier", label: "Carrier" },
  { value: "leased", label: "Leased" },
  { value: "independent_contractor", label: "Independent contractor" },
];
const fmtDeletedDate = (iso) => (iso ? String(iso).slice(0, 10) : "");

// The STS vehicle roster needs a VIN and an ownership type on top of what this form already
// required. Tell the person saving the record right away, at the moment the vehicle is approved.
const STS_VEHICLE_FIELDS = [
  { key: "vin", label: "VIN" },
  { key: "ownership", label: "ownership" },
];
// MnDOT gives an STS provider ten days from the change to notify it in writing (contract L).
// noticeDueDate is set only when this save actually opened a registration notice (a create, or
// a status change into/out of "retired"); the date is computed the same way the server does.
function mndotNoticeDueDate() {
  const d = new Date();
  d.setDate(d.getDate() + 10);
  return d.toLocaleDateString();
}

function stsVehicleToast(vehicle, noticeDueDate) {
  const missing = STS_VEHICLE_FIELDS.filter((f) => !vehicle?.[f.key]).map((f) => f.label);
  const fleetPart = vehicle?.fleet_number ? `Fleet number ${vehicle.fleet_number}, ` : "";
  const noticePart = noticeDueDate ? `, MnDOT notice due ${noticeDueDate}` : "";
  if (missing.length === 0) toast.success(`Saved. ${fleetPart}added to the STS vehicle list${noticePart}`);
  else toast.success(`Saved. ${fleetPart}but the STS list needs: ${missing.join(", ")}${noticePart}`);
}

// Lowest unused zero-padded two-digit fleet number ("01", "02", ...), growing to three digits
// past 99, counted only among non-retired non-deleted vehicles. Mirrors the server's own
// assignment so a blank "Add Vehicle" dialog already shows the number it expects to get.
function suggestFleetNumber(rows) {
  const used = new Set(
    rows.filter((v) => !v.deleted_at && v.status !== "retired" && v.fleet_number).map((v) => v.fleet_number)
  );
  let n = 1;
  while (true) {
    const candidate = String(n).padStart(2, "0");
    if (!used.has(candidate)) return candidate;
    n += 1;
  }
}

export default function Vehicles() {
  const { user } = useAuth();
  const isAdmin = user?.role === "agency_admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [fleetNumberError, setFleetNumberError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => api.get(`/vehicles${showDeleted ? "?include_deleted=true" : ""}`).then((r) => setRows(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showDeleted]);

  // Deep link from the Compliance "DOT inspection ready" panel: /app/vehicles?edit=<id> opens
  // that vehicle straight into the edit dialog so a missing field can be fixed in one click.
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || rows.length === 0) return;
    const v = rows.find((x) => x.id === editId);
    if (v) {
      setForm({ ...empty, ...v, wheelchair_positions: v.wheelchair_positions ?? "" });
      setEditingId(v.id);
      setOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const restore = async (id) => {
    try {
      await api.post(`/vehicles/${id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    setFleetNumberError("");
    try {
      const p = {
        ...form,
        year: Number(form.year) || null,
        capacity: Number(form.capacity) || 1,
        speed_limit_mph: form.speed_limit_mph === "" ? null : Number(form.speed_limit_mph),
        vin: form.vin || null,
        wheelchair_positions: form.wheelchair_positions === "" ? null : Number(form.wheelchair_positions),
        ownership: form.ownership || null,
        fleet_number: form.fleet_number || null,
        plate_state: form.plate_state || "MN",
        gvwr: form.gvwr || null,
      };
      // A create always opens an MnDOT "add" notice; an update only opens one when the status
      // crosses into or out of "retired" (mirrors the server's own hook logic, contract L).
      const priorStatus = editingId ? rows.find((v) => v.id === editingId)?.status || "active" : null;
      const opensNotice = editingId === null || (priorStatus !== "retired" && form.status === "retired") || (priorStatus === "retired" && form.status !== "retired");

      const { data } = editingId ? await api.put(`/vehicles/${editingId}`, p) : await api.post("/vehicles", p);
      stsVehicleToast(data, opensNotice ? mndotNoticeDueDate() : null);
      setOpen(false); load();
    } catch (e) {
      if (e.response?.data?.detail === "fleet_number_taken") {
        setFleetNumberError("That fleet number is already used by another active vehicle.");
        return;
      }
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="vehicles-page">
      <PageHeader
        subtitle="Fleet"
        title="Vehicles"
        action={
          <div className="flex items-center gap-4">
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} data-testid="vehicles-show-deleted" />
                Show deleted
              </label>
            )}
            <button
              onClick={() => { setForm({ ...empty, fleet_number: suggestFleetNumber(rows) }); setEditingId(null); setFleetNumberError(""); setOpen(true); }}
              className="btn-primary"
              data-testid="add-vehicle-btn"
            >
              + Add Vehicle
            </button>
          </div>
        }
      />
      <TableShell columns={["#", "Vehicle", "Plate", "Capacity", "Accessibility", "Limit", "Status", "Actions"]} empty={rows.length === 0 ? "No vehicles yet." : null}>
        {rows.map((v) => {
          const isDeleted = !!v.deleted_at;
          return (
            <tr key={v.id} className={`tbl-row border-t border-slate-100 ${isDeleted ? "opacity-60" : ""}`}>
              <td className="px-4 py-2.5">
                {v.fleet_number ? (
                  <span className="font-mono text-xs bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-2 py-0.5" data-testid={`fleet-number-${v.id}`}>
                    #{v.fleet_number}
                  </span>
                ) : (
                  <span className="text-slate-400 text-xs">-</span>
                )}
              </td>
              <td className="px-4 py-2.5">
                <div className="font-medium">{v.year} {v.make} {v.model}</div>
                {isDeleted && (
                  <span className="mt-1 inline-block bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 text-[10px]">
                    deleted {fmtDeletedDate(v.deleted_at)}
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 font-mono text-xs">{v.plate}</td>
              <td className="px-4 py-2.5">{v.capacity}</td>
              <td className="px-4 py-2.5 text-xs">
                {v.wheelchair_accessible && <span className="inline-block bg-sky-50 text-sky-800 border border-sky-200 rounded-full px-2 py-0.5 mr-1">Wheelchair</span>}
                {v.stretcher_capable && <span className="inline-block bg-violet-50 text-violet-800 border border-violet-200 rounded-full px-2 py-0.5">Stretcher</span>}
                {!v.wheelchair_accessible && !v.stretcher_capable && <span className="text-slate-400">-</span>}
              </td>
              <td className="px-4 py-2.5 font-mono text-xs">{v.speed_limit_mph ? `${v.speed_limit_mph} mph` : <span className="text-slate-400">-</span>}</td>
              <td className="px-4 py-2.5 capitalize">{v.status}</td>
              <td className="px-4 py-2.5 space-x-3">
                {isDeleted ? (
                  <button className="text-emerald-700 hover:underline text-xs" onClick={() => restore(v.id)} data-testid={`restore-${v.id}`}>Restore</button>
                ) : (
                  <>
                    <button className="text-[#005E82] hover:underline text-xs" onClick={() => { setForm({ ...empty, ...v }); setEditingId(v.id); setFleetNumberError(""); setOpen(true); }}>Edit</button>
                    <button className="text-rose-600 hover:underline text-xs" onClick={async () => { if (confirm("Delete this vehicle? This can be restored by an admin from Show deleted.")) { await api.delete(`/vehicles/${v.id}`); load(); } }}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </TableShell>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? "Edit Vehicle" : "New Vehicle"}
        footer={<>
          <button onClick={() => setOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
          <button onClick={save} className="btn-primary" data-testid="save-vehicle-btn">Save</button>
        </>}
      >
        <div className="space-y-4">
          <Field
            label="Fleet number"
            testId="v-fleet-number"
            value={form.fleet_number}
            onChange={(e) => { setForm({ ...form, fleet_number: e.target.value }); setFleetNumberError(""); }}
            maxLength={6}
            placeholder="e.g. 01"
          />
          {fleetNumberError && (
            <div className="text-xs text-rose-600 -mt-2" data-testid="v-fleet-number-error">{fleetNumberError}</div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Make" testId="v-make" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} />
            <Field label="Model" testId="v-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Year" testId="v-year" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
            <Field label="Plate #" testId="v-plate" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} />
          </div>
          <Field label="Capacity" testId="v-cap" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
          <Field label="Speed limit (mph)" testId="v-speed-limit" type="number" value={form.speed_limit_mph} onChange={(e) => setForm({ ...form, speed_limit_mph: e.target.value })} placeholder="e.g. 55" />
          <Field
            label="VIN"
            testId="v-vin"
            value={form.vin}
            onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
            maxLength={17}
            placeholder="17 characters"
          />
          {form.vin && form.vin.length > 0 && form.vin.length !== 17 && (
            <div className="text-xs text-rose-600 -mt-2" data-testid="v-vin-error">VIN must be 17 characters.</div>
          )}
          <div className="flex items-center gap-6 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.wheelchair_accessible} onChange={(e) => setForm({ ...form, wheelchair_accessible: e.target.checked })} /> Wheelchair</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.stretcher_capable} onChange={(e) => setForm({ ...form, stretcher_capable: e.target.checked })} /> Stretcher</label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Wheelchair equipment"
              testId="v-wc-equipment"
              value={form.wheelchair_equipment}
              onChange={(e) => {
                const equipment = e.target.value;
                setForm({ ...form, wheelchair_equipment: equipment, wheelchair_accessible: equipment !== "none" ? true : form.wheelchair_accessible });
              }}
              options={WHEELCHAIR_EQUIPMENT}
            />
            {form.wheelchair_equipment !== "none" && (
              <Field
                label="Wheelchair positions (not printed on the roster)"
                testId="v-wc-positions"
                type="number"
                min="0"
                value={form.wheelchair_positions}
                onChange={(e) => setForm({ ...form, wheelchair_positions: e.target.value })}
              />
            )}
          </div>
          <div className="flex items-center gap-6 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.protected_transport} onChange={(e) => setForm({ ...form, protected_transport: e.target.checked })} data-testid="v-protected" /> Protected transport
            </label>
          </div>
          <Field label="Ownership" testId="v-ownership" value={form.ownership} onChange={(e) => setForm({ ...form, ownership: e.target.value })} options={OWNERSHIP} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Plate state" testId="v-plate-state" value={form.plate_state} onChange={(e) => setForm({ ...form, plate_state: e.target.value })} options={PLATE_STATES} />
            <Field label="GVWR" testId="v-gvwr" value={form.gvwr} onChange={(e) => setForm({ ...form, gvwr: e.target.value })} placeholder="e.g. 9,900 lbs" />
          </div>
          <Field label="Status" testId="v-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={STATUS} />
        </div>
      </Drawer>
    </div>
  );
}
